/**
 * ELIO BLE packet layer — G3 ble_packet (TCP/UDP 윈도우 프로토콜) JS 포팅.
 * 정본: USBDongle-ESP32/main/packet/m_packet.c (원본 nRF5 mobilian/library/ble_packet.c)
 * 와이어 1바이트도 바꾸지 않는다. 헤더 1B (LSB first):
 *   DAT: bit0 flag=0 · bit1 method(0=TCP,1=UDP) · bit2-3 reserved · bit4-7 window
 *   CTL: bit0 flag=1 · bit1-3 cmd(0=WIN,1=ACK,2=RTR) · bit4-7 win
 *   WIN: [CTL hdr][start][ack]
 */

const DATA_LEN = 60; // m_packet.c:27 BLE_PACKET_DATA_LEN
const WINDOW_SIZE = 4; // m_packet.c:29
const SLOT_LEN = 8; // m_packet.c:30
const TIMEOUT_MS = 1000; // m_packet.c:31

const BPC_WIN = 0;
const BPC_ACK = 1;
const BPC_RTR = 2;

const HDR_UDP = 0x02; // flag=DAT, method=UDP, window=0

/**
 * DAT 헤더.
 * @param {boolean} udp UDP 여부
 * @param {number} window 윈도우 번호 0~15
 * @returns {number} 헤더 바이트
 */
const datHeader = function (udp, window) {
    return ((window & 0x0F) << 4) | (udp ? 0x02 : 0x00);
};

/**
 * CTL 헤더.
 * @param {number} cmd BPC_*
 * @param {number} win 윈도우 번호
 * @returns {number} 헤더 바이트
 */
const ctlHeader = function (cmd, win) {
    return ((win & 0x0F) << 4) | ((cmd & 0x07) << 1) | 0x01;
};

/**
 * 윈도우 범위 비교용 펼치기 — start 보다 작으면 한 바퀴 더한다 (m_packet.c 동일 규칙).
 * @param {number} start 기준
 * @param {number} v 값
 * @returns {number} 펼친 값
 */
const unwrap = function (start, v) {
    return start > v ? v + WINDOW_SIZE : v;
};

class ElioBlePacket {
    /**
     * @param {object} opts 옵션
     * @param {function(Uint8Array): boolean} opts.write 1패킷 송신. false = 지금 못 보냄(다음 run 재시도)
     * @param {function(Uint8Array)} opts.onTcp TCP 수신 (헤더 제거)
     * @param {function(Uint8Array)} opts.onUdp UDP 수신 (헤더 제거)
     * @param {function(): number} [opts.now] 시계(ms) — 시험용 주입
     */
    constructor (opts) {
        this._write = opts.write;
        this._onTcp = opts.onTcp;
        this._onUdp = opts.onUdp;
        this._now = opts.now || Date.now;
        this.reset();
    }

    reset () {
        this._slots = [];
        this._readPos = 0;
        this._writePos = 0;
        this._ackWindow = 0;
        this._remoteAck = 0;
        this._pendingAck = false;
        this._timer = this._now();
    }

    get _startWindow () {
        return this._readPos % WINDOW_SIZE;
    }

    get _sentCount () {
        const start = this._startWindow;
        return this._ackWindow >= start ?
            this._ackWindow - start :
            WINDOW_SIZE - (start - this._ackWindow);
    }

    /**
     * 신뢰 송신 — 60B 슬롯으로 잘라 fifo 에 넣는다. 실제 송신은 run().
     * @param {Uint8Array|Array<number>} data 데이터
     * @returns {boolean} 공간 없으면 false
     */
    tcpSend (data) {
        const free = SLOT_LEN - (this._writePos - this._readPos);
        if (data.length > free * DATA_LEN) return false;
        for (let off = 0; off < data.length; off += DATA_LEN) {
            this._slots[this._writePos % SLOT_LEN] = Uint8Array.from(data.slice(off, off + DATA_LEN));
            this._writePos++;
        }
        return true;
    }

    /**
     * 즉시 송신 (손실 허용).
     * @param {Uint8Array|Array<number>} data 데이터 (60B 이하)
     * @returns {boolean} 송신 결과
     */
    udpSend (data) {
        if (data.length > DATA_LEN) return false;
        const p = new Uint8Array(data.length + 1);
        p[0] = HDR_UDP;
        p.set(data, 1);
        return this._write(p);
    }

    /** 주기 처리 (동글은 50ms) — 송신 윈도우 · 타임아웃 WIN · 받은 TCP 의 ACK. */
    run () {
        this._transfer();
        this._checkControl();
        this._pendingWork();
    }

    _transfer () {
        let sent = this._sentCount;
        const count = this._writePos - this._readPos;
        while (sent < WINDOW_SIZE - 1 && sent < count) {
            const slot = this._slots[(this._readPos + sent) % SLOT_LEN];
            const p = new Uint8Array(slot.length + 1);
            p[0] = datHeader(false, this._ackWindow);
            p.set(slot, 1);
            if (!this._write(p)) return;
            sent++;
            this._ackWindow = (this._ackWindow + 1) % WINDOW_SIZE;
            this._timer = this._now();
        }
    }

    _checkControl () {
        if (this._ackWindow === this._startWindow) return;
        if (this._now() - this._timer < TIMEOUT_MS) return;
        const p = new Uint8Array([ctlHeader(BPC_WIN, 0), this._startWindow, this._ackWindow]);
        if (this._write(p)) this._timer = this._now();
    }

    _pendingWork () {
        if (!this._pendingAck) return;
        if (this._write(new Uint8Array([ctlHeader(BPC_ACK, this._remoteAck)]))) {
            this._pendingAck = false;
        }
    }

    /**
     * NUS notify 진입점.
     * @param {Uint8Array} p 수신 바이트
     */
    receive (p) {
        if (p.length < 1) return;
        const h = p[0];
        if (h & 0x01) {
            this._control((h >> 1) & 0x07, (h >> 4) & 0x0F, p);
            return;
        }
        if (h & 0x02) {
            this._onUdp(p.subarray(1));
            return;
        }
        if (((h >> 4) & 0x0F) === this._remoteAck) {
            this._remoteAck = (this._remoteAck + 1) % WINDOW_SIZE;
            this._pendingAck = true;
            this._onTcp(p.subarray(1));
        } else {
            this._write(new Uint8Array([ctlHeader(BPC_RTR, this._remoteAck)]));
            this._timer = this._now();
        }
    }

    _control (cmd, win, p) {
        const start = this._startWindow;
        if (cmd === BPC_WIN) {
            if (p.length < 3) return;
            const s = p[1];
            const ack = unwrap(s, p[2]);
            const rack = unwrap(s, this._remoteAck);
            if (s <= rack && rack <= ack) {
                this._write(new Uint8Array([ctlHeader(rack < ack ? BPC_RTR : BPC_ACK, this._remoteAck)]));
            }
            return;
        }
        const ack = unwrap(start, this._ackWindow);
        const w = unwrap(start, win);
        if (cmd === BPC_ACK && start <= w && w <= ack) {
            this._readPos += w - start;
            this._timer = this._now();
        } else if (cmd === BPC_RTR && start <= w && w < ack) {
            this._ackWindow = w % WINDOW_SIZE;
            this._timer = this._now();
        }
    }
}

module.exports = {
    ElioBlePacket,
    datHeader,
    ctlHeader,
    HDR_UDP,
    DATA_LEN,
    WINDOW_SIZE
};
